import { Helmet } from "react-helmet-async";

const SITE_NAME = "Sahyog Finance & Noble Infinity";
const LEGAL_NAME = "Sahyog Finance & Noble Infinity Asset Management Pvt. Ltd.";
const SITE_URL = "https://nobleinfinityassetmanagementsahyog.com";
const ALT_SITE_URL = "https://www.sahyogtapi.in";
const LOGO_URL = `${SITE_URL}/sahyog-logo.png`;
const DEFAULT_IMAGE = `${SITE_URL}/og-image.png`;

/* ─── Organization JSON-LD (schema.org) ────────────────────────────
 * Rendered on every page so Google can render our logo next to
 * the site name in search results ("Sitelinks searchbox + logo"
 * rich result). Uses FinancialService as the more-specific type
 * with Organization as the parent.
 * ────────────────────────────────────────────────────────────── */
const ORGANIZATION_SCHEMA = {
  "@context": "https://schema.org",
  "@type": ["Organization", "FinancialService"],
  "@id": `${SITE_URL}/#organization`,
  name: LEGAL_NAME,
  alternateName: ["Sahyog Finance", "Noble Infinity Asset Management", "Noble Infinity"],
  legalName: LEGAL_NAME,
  url: SITE_URL,
  logo: {
    "@type": "ImageObject",
    url: LOGO_URL,
    width: 1080,
    height: 1059,
  },
  image: LOGO_URL,
  description:
    "Sahyog Finance & Noble Infinity Asset Management — 14 loan products (Personal, Business, Agriculture, Mortgage, Housing, Cattle, FD, Credit Card, Social, Plot, Gold, Loan Against Security Deposit) starting at 6.5%. No CIBIL required. Trusted lending across Gujarat since 2021.",
  foundingDate: "2021",
  areaServed: { "@type": "Country", name: "India" },
  address: {
    "@type": "PostalAddress",
    streetAddress: "A-106, Ambaji Complex, Unai Road",
    addressLocality: "Vyara",
    addressRegion: "Gujarat",
    postalCode: "394650",
    addressCountry: "IN",
  },
  contactPoint: [
    {
      "@type": "ContactPoint",
      telephone: "+91-70169-75324",
      contactType: "customer service",
      areaServed: "IN",
      availableLanguage: ["English", "Hindi", "Gujarati"],
    },
    {
      "@type": "ContactPoint",
      telephone: "+91-98796-30926",
      contactType: "sales",
      areaServed: "IN",
      availableLanguage: ["English", "Hindi", "Gujarati"],
    },
  ],
  email: "sahyogpfir@gmail.com",
  sameAs: [
    ALT_SITE_URL,
    "https://www.facebook.com/sahyogfinance",
    "https://www.linkedin.com/company/sahyog-finance",
    "https://www.instagram.com/sahyog_finance",
    "https://twitter.com/sahyogfinance",
    "https://wa.me/917016975324",
  ],
};

const WEBSITE_SCHEMA = {
  "@context": "https://schema.org",
  "@type": "WebSite",
  "@id": `${SITE_URL}/#website`,
  url: SITE_URL,
  name: SITE_NAME,
  publisher: { "@id": `${SITE_URL}/#organization` },
  inLanguage: "en-IN",
  potentialAction: {
    "@type": "SearchAction",
    target: `${SITE_URL}/?q={search_term_string}`,
    "query-input": "required name=search_term_string",
  },
};

/**
 * Per-route SEO tags. Use exactly one <Seo /> per page.
 * - title: rendered as "<title> · Sahyog Finance & Noble Infinity"
 * - description: 150–160 char meta description (unique per page)
 * - path: relative path, e.g. "/careers" — used for canonical
 * - noindex: set true for admin/partner-portal pages
 */
export default function Seo({
  title,
  description,
  path = "/",
  image = DEFAULT_IMAGE,
  noindex = false,
}) {
  const fullTitle = title ? `${title} · ${SITE_NAME}` : `${SITE_NAME} — Smart Finance Solutions`;
  const canonical = `${SITE_URL}${path}`;
  return (
    <Helmet>
      <title>{fullTitle}</title>
      {description && <meta name="description" content={description} />}
      <link rel="canonical" href={canonical} />
      {noindex && <meta name="robots" content="noindex,nofollow" />}
      {/* Open Graph */}
      <meta property="og:type" content="website" />
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:title" content={fullTitle} />
      {description && <meta property="og:description" content={description} />}
      <meta property="og:url" content={canonical} />
      <meta property="og:image" content={image} />
      <meta property="og:logo" content={LOGO_URL} />
      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      {description && <meta name="twitter:description" content={description} />}
      <meta name="twitter:image" content={image} />
      {/* Structured data — Organization + WebSite (JSON-LD)
          Helps Google render our logo next to the site name in SERPs. */}
      <script type="application/ld+json">
        {JSON.stringify(ORGANIZATION_SCHEMA)}
      </script>
      <script type="application/ld+json">
        {JSON.stringify(WEBSITE_SCHEMA)}
      </script>
    </Helmet>
  );
}
